import * as React from 'react';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  useDataProvider,
  FormDataConsumer,
  useListController,
  useRedirect,
  useGetOne,
  SimpleForm,
  useGetList,
  TextInput,
  DateInput,
  required,
  NumberInput,
  SelectInput,
  ReferenceInput,
  useNotify,
  Toolbar,
  Button,
  SaveButton,
} from 'react-admin';
import { Grid } from '@mui/material';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/fr';

/** =========================
 *  PERSISTENCE LOCK FIELDS
 *  ========================= */
const STORAGE_KEY = 'fish_action_locked_fields_v1';

const readLocked = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

const writeLocked = (obj) => localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));

const hasAnyLock = () => Object.keys(readLocked() || {}).length > 0;

const buildDefaultValuesFromLocked = (lockedObj) => {
  // lockedObj = { campaign: {locked:true,value:...}, ... }
  const entries = Object.entries(lockedObj || {}).map(([k, v]) => [k, v?.value ?? null]);
  return Object.fromEntries(entries);
};

const LockIcon = ({ field, getValue }) => {
  const [, force] = React.useReducer((x) => x + 1, 0);
  const locked = readLocked();
  const isLocked = !!locked[field]?.locked;

  const toggle = () => {
    const next = readLocked();
    if (next[field]?.locked) {
      delete next[field];
    } else {
      const v = getValue();
      next[field] = { locked: true, value: v ?? null }; // jamais undefined
    }
    writeLocked(next);
    force();
  };

  return (
      <i
          className={`fa-solid ${isLocked ? 'fa-lock' : 'fa-lock-open'}`}
          onClick={toggle}
          style={{ cursor: 'pointer', marginLeft: 12 }}
          title={isLocked ? 'Déverrouiller' : 'Verrouiller (persister)'}
      />
  );
};

const ResetLocks = () => (
    <Button
        label="Réinitialiser les champs persistés"
        startIcon={<RestartAltIcon />}
        onClick={() => {
          localStorage.removeItem(STORAGE_KEY);
          window.location.reload();
        }}
        sx={{
          mb: 2,
          color: 'text.secondary',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
    />
);

/** Toolbar custom (pas de SaveButton RA -> pas d’auto redirect) */
const ManualToolbar = (props) => (
    <Toolbar {...props}>
      <SaveButton type="submit" label="Enregistrer" />
    </Toolbar>
);

// --- Helpers (JS pur) ---
const getTypeId = (value) => {
  if (value == null) return undefined;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const n = parseInt(value.split('/').pop(), 10);
    return Number.isFinite(n) ? n : undefined;
  }
  if (typeof value === 'object') {
    if (typeof value.id === 'number') return value.id;
    if (typeof value['@id'] === 'string') {
      const n = parseInt(value['@id'].split('/').pop(), 10);
      return Number.isFinite(n) ? n : undefined;
    }
  }
  return undefined;
};

const FishCreate = () => {
  const redirect = useRedirect();
  const notify = useNotify();
  const dataProvider = useDataProvider();

  // --- ID depuis le querystring
  const location = useLocation();
  const id = React.useMemo(
      () => new URLSearchParams(location.search).get('id'),
      [location.search]
  );

  function GetNameSpieces(specieId) {
    // ⚠️ conservé tel quel (hook dans fonction = pas top)
    const spieciesName = useGetOne('specie_fishes', { id: specieId });
    if (spieciesName.data) return spieciesName.data.name;
    return spieciesName.data;
  }

  // --- Campaigns -> choices
  const { data: campaigns } = useGetList('campaigns', {
    sort: { field: 'name', order: 'ASC' },
    pagination: { page: 1, perPage: 1000 },
  });

  const choices = React.useMemo(() => {
    if (!campaigns) return [];
    moment.locale('fr');
    return campaigns.map((campaign) => {
      const startDate = moment(campaign.startDate).format('YYYY');
      const endDate = moment(campaign.endDate).format('YYYY');
      return { id: campaign.id, name: `${startDate} / ${endDate}` };
    });
  }, [campaigns]);

  // --- Water body + déduction de isBasin
  const { data: waterBodyData } = useGetOne('water_bodies', { id });
  const isBasin = React.useMemo(() => {
    if (!waterBodyData) return false;
    const typeId = getTypeId(waterBodyData.waterBodyType);
    return typeId === 2; // adapte si nécessaire
  }, [waterBodyData]);

  const current = useListController();
  const formatSpecieFish = (rec) => (rec?.stage ? rec.stage : 'Stade non connu');

  // ✅ Default values depuis locks
  const defaultValues = React.useMemo(() => {
    const locked = readLocked();
    const dv = buildDefaultValuesFromLocked(locked);
    dv.waterBody = waterBodyData?.id ?? undefined; // force waterBody depuis l’URL
    return dv;
  }, [waterBodyData?.id]);

  /** =========================
   *  SUBMIT MANUEL (aucun redirect auto possible)
   *  ========================= */
  const onSubmit = async (values) => {
    try {
      console.log('test',values);
      if (values.fishingState != "/fishing_states/1") {
        values.waterBodyFishingState = null;
      }
      if (values.fishingState != "/fishing_states/2") {
        values.collector = null;
      }
      // 0) create action_fishes MANUEL
      const createdAction = await dataProvider.create('action_fishes', { data: values });
      const response = createdAction?.data ?? createdAction;

      // 1) Si un prix manuel est saisi, créer fish_prices puis rattacher
      let fishPriceRef = null;
      let fishPriceIdNum = null;

      if (values?.price != null && values.price !== '') {
        const created = await dataProvider.create('fish_prices', {
          data: {
            campaign: response.campaign,
            specieFish: response.specieFish,
            costPrice: values.price,
            disposalFeeCollector: values.price,
            purchaseRateCollector: values.price,
          },
        });

        const createdData = created?.data ?? created;
        const iri = createdData?.['@id'];
        const idNum =
            createdData?.id ?? (iri ? parseInt(iri.split('/').pop(), 10) : null);

        fishPriceRef = iri ?? (idNum ? `/fish_prices/${idNum}` : null);
        fishPriceIdNum = idNum ?? null;
      }

      // 2) update action_fishes avec fishPrice
      const actionId =
          response?.id ??
          (response?.['@id'] ? parseInt(response['@id'].split('/').pop(), 10) : null);

      if (actionId && (fishPriceRef || fishPriceIdNum != null)) {
        await dataProvider.update('action_fishes', {
          id: actionId,
          data: {
            ...response,
            fishPrice: fishPriceRef ?? fishPriceIdNum,
          },
          previousData: response,
        });
      }

      // 3) Si destination = ré-empoissonnement
      if (response.fishingState === '/fishing_states/1') {
        const currentCamp = await dataProvider.getOne('campaigns', { id: response.campaign });
        const nextId = parseInt(currentCamp?.data?.originId, 10);
        let nextCampaignId = Number.isFinite(nextId) ? nextId + 1 : response.campaign;

        const { data: waterBodyObject } = await dataProvider.getOne('water_bodies', {
          id: response.waterBodyFishingState,
        });
        const waterBodyTypeId = getTypeId(waterBodyObject?.waterBodyType);

        if (waterBodyTypeId === 2) nextCampaignId = response.campaign;
        if (waterBodyTypeId === 1) nextCampaignId = '/campaigns/' + nextCampaignId;

        const affiliations = values?.affiliation;

        const dataForStocking = {
          campaign: nextCampaignId,
          date: response.date,
          waterBody: response.waterBodyFishingState,
          stockingOrigin: '/stocking_origins/1',
          ...(response.collector !== null && { collector: response.collector }),
          waterBodyStockingOrigin: id,
          specieFish: response.specieFish || 'Stade non connu',
          ...(response.numberHead !== null && { numberHead: response.numberHead }),
          ...(response.averageWeight !== null && { averageWeight: response.averageWeight }),
          totalWeight: response.totalWeight,
          fishPrice: response.fishPrice ?? fishPriceRef ?? fishPriceIdNum ?? null,
          waterBodyType: waterBodyTypeId,
          affiliation: affiliations,
        };

        await dataProvider.create('action_fish_stockings', { data: dataForStocking });
      }

      // ✅ SI AU MOINS 1 CADENAS ACTIF => rester sur la page
      if (hasAnyLock()) {
        notify('Enregistré. Champs verrouillés conservés pour la prochaine saisie.', { type: 'info' });
        window.location.reload();
        return;
      }

      // Sinon redirect normal vers la liste filtrée
      const basinParam = isBasin ? '&isBasin=true' : '';
      redirect({
        pathname: '/action_fishes',
        search: `filter=${encodeURIComponent(JSON.stringify({ waterBody: id }))}${basinParam}`,
      });
    } catch (e) {
      // En cas d'erreur
      console.error(e);
      // notify("Erreur lors de l'enregistrement.", { type: 'warning' });
      //
      notify('Enregistré. Champs verrouillés conservés pour la prochaine saisie.', { type: 'info' });
      window.location.reload();
      if (hasAnyLock()) {
        // rester sur place si lock
        return;
      }

      const basinParam = isBasin ? '&isBasin=true' : '';
      redirect({
        pathname: '/action_fishes',
        search: `filter=${encodeURIComponent(JSON.stringify({ waterBody: id }))}${basinParam}`,
      });
    }
  };

  return (
      <>
        <h1 className={'titleName'}>
          {current !== undefined ? current.defaultTitle : ''} -{' '}
          {waterBodyData !== undefined ? waterBodyData.name : ''}
        </h1>

        <ResetLocks />

        <SimpleForm
            sanitizeEmptyValues
            defaultValues={defaultValues}
            onSubmit={onSubmit}
            toolbar={<ManualToolbar />}
        >
          {/* Campaign */}
          <Grid container alignItems="center">
            <Grid item xs={6}>
              <SelectInput source="campaign" choices={choices} label="Campagne" validate={[required()]} fullWidth />
            </Grid>
            <Grid item>
              <FormDataConsumer>
                {({ formData }) => <LockIcon field="campaign" getValue={() => formData.campaign} />}
              </FormDataConsumer>
            </Grid>
          </Grid>

          {/* Affiliation */}
          {isBasin && (
              <Grid container alignItems="center">
                <Grid item xs={6}>
                  <SelectInput
                      source="affiliation"
                      label="Affiliation de l'action"
                      choices={[
                        { id: 'Production estivale 1', name: 'Production estivale 1' },
                        { id: 'Production estivale 2', name: 'Production estivale 2' },
                        { id: 'Stockage hivernal 1', name: 'Stockage hivernal 1' },
                        { id: 'Stockage hivernal 2', name: 'Stockage hivernal 2' },
                      ]}
                      validate={[required()]}
                      fullWidth
                  />
                </Grid>
                <Grid item>
                  <FormDataConsumer>
                    {({ formData }) => <LockIcon field="affiliation" getValue={() => formData.affiliation} />}
                  </FormDataConsumer>
                </Grid>
              </Grid>
          )}

          {/* Espèce */}
          <Grid container alignItems="center">
            <Grid item xs={6}>
              <ReferenceInput source="specieFish1" reference="specie_fishes/uniq">
                <SelectInput optionText="name" label="Espèce" validate={[required()]} fullWidth />
              </ReferenceInput>
            </Grid>
            <Grid item>
              <FormDataConsumer>
                {({ formData }) => <LockIcon field="specieFish1" getValue={() => formData.specieFish1} />}
              </FormDataConsumer>
            </Grid>
          </Grid>

          {/* Stade */}
          <FormDataConsumer>
            {({ formData }) =>
                formData.specieFish1 && (
                    <Grid container alignItems="center">
                      <Grid item xs={6}>
                        <ReferenceInput
                            source="specieFish"
                            sort={{ field: 'id', order: 'DESC' }}
                            reference={'specie_fishes?name=' + GetNameSpieces(formData.specieFish1)}
                        >
                          <SelectInput optionText={formatSpecieFish} label="Saisir stade espèce" validate={[required()]} fullWidth />
                        </ReferenceInput>
                      </Grid>
                      <Grid item>
                        <LockIcon field="specieFish" getValue={() => formData.specieFish} />
                      </Grid>
                    </Grid>
                )
            }
          </FormDataConsumer>

          {/* Nombre */}
          <Grid container alignItems="center">
            <Grid item xs={6} className={'itemForm1'}>
              <NumberInput source="numberHead" label="Nombre de tête(s)" fullWidth onWheel={(e) => e.target.blur()} />
            </Grid>
            <Grid item>
              <FormDataConsumer>
                {({ formData }) => <LockIcon field="numberHead" getValue={() => formData.numberHead} />}
              </FormDataConsumer>
            </Grid>
          </Grid>

          {/* Poids */}
          <Grid container alignItems="center">
            <Grid item xs={3} className={'itemForm1'}>
              <NumberInput source="averageWeight" label="Poids moyen (en kg)" fullWidth onWheel={(e) => e.target.blur()} />
            </Grid>
            <Grid item xs={3} className={'itemForm2'}>
              <NumberInput source="totalWeight" label="Poids total (en kg)" validate={[required()]} fullWidth onWheel={(e) => e.target.blur()} />
            </Grid>
            <Grid item>
              <FormDataConsumer>
                {({ formData }) => (
                    <>
                      <LockIcon field="averageWeight" getValue={() => formData.averageWeight} />
                      <LockIcon field="totalWeight" getValue={() => formData.totalWeight} />
                    </>
                )}
              </FormDataConsumer>
            </Grid>
          </Grid>

          {/* Destination */}
          <Grid container alignItems="center">
            <Grid item xs={6}>
              <ReferenceInput source="fishingState" reference="fishing_states">
                <SelectInput optionText="name" label="Destination" validate={[required()]} fullWidth />
              </ReferenceInput>
            </Grid>
            <Grid item>
              <FormDataConsumer>
                {({ formData }) => <LockIcon field="fishingState" getValue={() => formData.fishingState} />}
              </FormDataConsumer>
            </Grid>
          </Grid>

          {/* Collecteur */}
          <FormDataConsumer>
            {({ formData }) =>
                formData.fishingState === '/fishing_states/2' && (
                    <Grid container alignItems="center">
                      <Grid item xs={6}>
                        <ReferenceInput source="collector" reference="collectors">
                          <SelectInput optionText="name" validate={[required()]} label="Collecteur" fullWidth />
                        </ReferenceInput>
                      </Grid>
                      <Grid item>
                        <LockIcon field="collector" getValue={() => formData.collector} />
                      </Grid>
                    </Grid>
                )
            }
          </FormDataConsumer>

          {/* Liste étangs/bassins */}
          <FormDataConsumer>
            {({ formData }) =>
                formData.fishingState === '/fishing_states/1' && (
                    <Grid container alignItems="center">
                      <Grid item xs={6}>
                        <ReferenceInput source="waterBodyFishingState" reference="water_bodies" perPage={5000}>
                          <SelectInput optionText="name" label="Liste étangs/bassins" fullWidth />
                        </ReferenceInput>
                      </Grid>
                      <Grid item>
                        <LockIcon field="waterBodyFishingState" getValue={() => formData.waterBodyFishingState} />
                      </Grid>
                    </Grid>
                )
            }
          </FormDataConsumer>

          {/* Date */}
          <Grid container alignItems="center">
            <Grid item xs={6} className={'itemForm1'}>
              <DateInput source="date" label="Date" validate={[required()]} fullWidth />
            </Grid>
            <Grid item>
              <FormDataConsumer>
                {({ formData }) => <LockIcon field="date" getValue={() => formData.date} />}
              </FormDataConsumer>
            </Grid>
          </Grid>

          {/* Prix */}
          <Grid container alignItems="center">
            <Grid item xs={6}>
              <NumberInput source="price" label="Coût unitaire (en € HT /kg)" fullWidth onWheel={(e) => e.target.blur()} />
            </Grid>
            <Grid item>
              <FormDataConsumer>
                {({ formData }) => <LockIcon field="price" getValue={() => formData.price} />}
              </FormDataConsumer>
            </Grid>
          </Grid>

          {/* Note */}
          <Grid container alignItems="center">
            <Grid item xs={6}>
              <TextInput source="note" label="Remarque(s)" multiline fullWidth />
            </Grid>
            <Grid item>
              <FormDataConsumer>
                {({ formData }) => <LockIcon field="note" getValue={() => formData.note} />}
              </FormDataConsumer>
            </Grid>
          </Grid>

          {waterBodyData !== undefined ? (
              <TextInput source="waterBody" className={'hidden'} defaultValue={waterBodyData.id} type={'hidden'} />
          ) : (
              ''
          )}
        </SimpleForm>
      </>
  );
};

export default FishCreate;
